import { DiscIcon } from '@/components/Icons';
import { useDimensions } from '@/hooks/useDimensions';
import { UserPermissionsEnum, useSessionStore } from '@/hooks/useSessionStore';
import useGeneratorStore from '@/state/generator-store';
import { cn } from '@/utils';
import React from 'react';
import TrackButton from './TrackButton';

export const MaxTracksMap = {
  [UserPermissionsEnum.Novice]: 1,
  [UserPermissionsEnum.Dabbler]: 1,
  [UserPermissionsEnum.Soloist]: 5,
  [UserPermissionsEnum.Pro]: 25,
};

const TrackControl: React.FC = () => {
  const { tracksCount, setTracksCount } = useGeneratorStore();

  const { session } = useSessionStore();

  const maxTracks = MaxTracksMap[session?.user?.plan?.key as UserPermissionsEnum];

  const handleIncrement = (): void => setTracksCount((prev: number) => Math.min(maxTracks, prev + 1));
  const handleDecrement = (): void => setTracksCount((prev: number) => Math.max(1, prev - 1));

  const totalStripes = 13;
  const activeStripes = Math.floor(((tracksCount + 1) / maxTracks) * totalStripes);

  const { isMobileOrPad } = useDimensions();

  if (isMobileOrPad) {
    return (
      <div
        className="flex-col p-1 h-full w-[56px] bg-black items-start justify-center rounded-r-[20px] pointer-events-auto"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <div className="flex items-center ml-1">
          <DiscIcon className="w-2 h-2" />
          <div className="flex gap-[1px]">
            {Array.from({ length: totalStripes }).map((_, index) => (
              <div key={index} className={`w-[1px] h-2 rounded-sm ${index < activeStripes ? 'bg-white' : 'bg-white/20'}`} />
            ))}
          </div>
        </div>

        <div className="flex items-center h-6">
          <TrackButton
            direction="decrease"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleDecrement();
            }}
          />
          <span className="text-[1rem] w-[28px] font-medium text-center text-white">{tracksCount}</span>
          <TrackButton
            direction="increase"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleIncrement();
            }}
          />
        </div>

        <div className="flex items-center mt-[-4px] ml-1">
          <span className="text-[#e0e4e4] text-[9px] font-normal font-pressura-mono uppercase">
            TRACK<span className={cn({ 'opacity-30': tracksCount <= 1 })}>S</span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex-col p-1 h-full w-[56px] bg-black items-start justify-center rounded-r-[20px] pointer-events-auto"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <div className="flex items-center">
        <DiscIcon className="w-3 h-3" />
        <div className="flex gap-[1px]">
          {Array.from({ length: totalStripes }).map((_, index) => (
            <div key={index} className={`w-[1px] h-3 rounded-sm ${index < activeStripes ? 'bg-white' : 'bg-white/20'}`} />
          ))}
        </div>
      </div>

      <div className="flex items-center h-9">
        <TrackButton
          direction="decrease"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleDecrement();
          }}
        />
        <span className="text-[1.6rem] w-[32px] font-medium text-center">{tracksCount}</span>
        <TrackButton
          direction="increase"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleIncrement();
          }}
        />
      </div>

      <div className="Tracks leading-3">
        <span className="text-[#e0e4e4] text-[12px] font-normal font-pressura-mono uppercase">
          TRACK<span className={cn({ 'opacity-30': tracksCount <= 1 })}>S</span>
        </span>
      </div>
    </div>
  );
};

export default TrackControl;
